// Settings → Legal panel.
// Shows the user's accepted ToS+Privacy version + acceptance date + links to
// the current versions at /legal/terms and /legal/privacy.
//
// Reads from the same shared legal/version.js constants and the same
// users/{uid}.tos_accepted_* fields the LegalAcceptanceGate writes on accept.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../FirebaseConfig';
import { LEGAL_VERSION, LEGAL_LAST_UPDATED } from '../legal/version';

const formatAcceptedAt = (timestamp) => {
  if (!timestamp) return null;
  // Firestore Timestamp objects expose seconds; allow Date objects too.
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toLocaleDateString();
  }
  if (typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000).toLocaleDateString();
  }
  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString();
  }
  return null;
};

const Legal = ({ currentUser }) => {
  const [acceptedVersion, setAcceptedVersion] = useState(undefined);
  const [acceptedAt, setAcceptedAt] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data();
          setAcceptedVersion(data.tos_accepted_version);
          setAcceptedAt(data.tos_accepted_at);
        }
      } catch (err) {
        console.warn('Settings → Legal: failed to load user doc', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const formattedDate = formatAcceptedAt(acceptedAt);

  return (
    <div style={{ padding: '0 8px' }}>
      <h3 style={{ marginTop: 0 }}>Legal</h3>
      <p>
        <strong>Current Terms of Use & Privacy Policy:</strong>{' '}
        {LEGAL_VERSION} (last updated {LEGAL_LAST_UPDATED}).
      </p>
      <p>
        <strong>You accepted:</strong>{' '}
        {!loaded
          ? 'Loading…'
          : acceptedVersion
          ? `${acceptedVersion}${formattedDate ? ` on ${formattedDate}` : ''}`
          : 'No acceptance record on file.'}
      </p>
      <ul>
        <li>
          <Link to="/legal/terms" target="_blank" rel="noopener noreferrer">
            Read the current Terms of Use
          </Link>
        </li>
        <li>
          <Link to="/legal/privacy" target="_blank" rel="noopener noreferrer">
            Read the current Privacy Policy
          </Link>
        </li>
      </ul>
      <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
        Operator: Maia AI, LLC. Questions: support@maiatech.ai.
      </p>
    </div>
  );
};

export default Legal;
